/**
 * G3 — same-tab bridge so `/dev/login` cold-start does not double-hit GET /admin/me.
 *
 * AuthProvider owns the cookie-first boot `/me`. AdminPwaColdStart awaits that
 * identity result when an owner is mounted, instead of issuing a parallel raw fetch.
 */

type IdentityWaiter = (sessionLive: boolean) => void;

let ownerCount = 0;
let published: boolean | undefined;
let waiters: IdentityWaiter[] = [];

export function claimPlatformMeBootOwner(): void {
  ownerCount += 1;
}

export function releasePlatformMeBootOwner(): void {
  ownerCount = Math.max(0, ownerCount - 1);
  if (ownerCount === 0) {
    published = undefined;
  }
}

export function hasPlatformMeBootOwner(): boolean {
  return ownerCount > 0;
}

/**
 * AuthProvider boot settlement — sessionLive when cookie `/me` hydrated a user.
 * Idempotent for waiters: first publish wins; later calls refresh the cached value.
 */
export function publishPlatformMeBootIdentity(sessionLive: boolean): void {
  published = sessionLive;
  const pending = waiters;
  waiters = [];
  for (const waiter of pending) {
    waiter(sessionLive);
  }
}

/** ColdStart on `/dev/login` — join AuthProvider boot; never starts its own /me when owner is present. */
export function awaitPlatformMeBootIdentity(): Promise<boolean> {
  if (published !== undefined) {
    return Promise.resolve(published);
  }
  return new Promise((resolve) => {
    waiters.push(resolve);
  });
}

export function resetPlatformMeBootBridgeForTests(): void {
  ownerCount = 0;
  published = undefined;
  waiters = [];
}
